import React, { useEffect, useLayoutEffect, useState } from 'react';
import './DojoInvitePage.css';
import DojoCoachMembershipNotice from '../components/DojoCoachMembershipNotice';
import useAccountActionScope from '../hooks/useAccountActionScope';
import DojoJoinSettingsConsent, { joinSettingChanges, joinConsentOptions } from '../components/DojoJoinSettingsConsent';


function DojoInvitePage({ backendService, isSignedIn, userSynced }) {
  const token = new URLSearchParams(window.location.search).get('token');
  const [state, setState] = useState({ status: 'loading', info: null, currentDojo: null, error: '' });
  const [joining, setJoining] = useState(false);
  const [eligibility, setEligibility] = useState(null);
  const [acceptCoachSettings, setAcceptCoachSettings] = useState(false);
  const [acceptRequiredSettings, setAcceptRequiredSettings] = useState(false);
  const [acceptBackground, setAcceptBackground] = useState(false);
  const captureAccount = useAccountActionScope(backendService, token);
  const [inviteKind, setInviteKind] = useState('link');
  useLayoutEffect(() => { setEligibility(null); setAcceptRequiredSettings(false); setAcceptBackground(false); setAcceptCoachSettings(false); setJoining(false); setState({ status: 'loading', info: null, error: '' }); }, [captureAccount]);

  useEffect(() => {
    if (!token) {
      setState({ status: 'error', error: 'This dojo invite link is missing its token.' });
      return;
    }
    if (!isSignedIn || !userSynced) return;

    let cancelled = false;
    const account = captureAccount();
    setEligibility(null); setAcceptRequiredSettings(false); setAcceptBackground(false);
    const load = async () => {
      try {
        let info, kind = 'link';
        try {
          info = await backendService.getDojoJoinLinkInfo(token);
          if (info?.valid === false) throw Object.assign(new Error('Invalid link token'), { response: { status: 400 } });
        } catch (error) {
          if (![400, 404].includes(error.response?.status)) throw error;
          info = await backendService.getDojoEmailInviteInfo(token);
          kind = 'email';
        }
        if (info?.valid === false || !info?.dojo) throw Object.assign(new Error('Invalid invite token'), { response: { data: { error: info?.error || 'This dojo invite link is invalid or expired.' } } });
        account.assert();
        const currentDojo = await backendService.getMyDojo();
        account.assert();
        const joinEligibility = info?.dojo?._id
          ? await backendService.checkDojoEligibility(info.dojo._id)
          : null;
        if (!cancelled && account.current()) {
          setEligibility({ ...joinEligibility, account, requirements: joinEligibility?.requirements || info.dojo.requirements, requirementsFingerprint: joinEligibility?.requirementsFingerprint || info.dojo.requirementsFingerprint, requiredSettingsDelta: joinEligibility?.requiredSettingsDelta || info.requiredSettingsDelta });
          setInviteKind(kind);
          setAcceptRequiredSettings(false); setAcceptBackground(false);
          setAcceptCoachSettings(!(joinEligibility?.suggestedCoachAutoAcceptance && Object.keys(joinEligibility.suggestedCoachAutoAcceptance).length > 0));
          setState({ status: 'ready', info, currentDojo, error: '' });
        }
      } catch (error) {
        if (!cancelled && account.current()) {
          setState({
            status: 'error',
            error: error.response?.data?.error || 'This dojo invite link is invalid or expired.'
          });
        }
      }
    };
    load();
    return () => { cancelled = true; };
  }, [backendService, isSignedIn, token, userSynced, backendService.authenticatedIdentity, backendService.positionOwner, backendService.backgroundIdentityGeneration, captureAccount]);

  const join = async () => {
    const consent = joinSettingChanges(eligibility);
    if (!eligibility || consent.blocking || (consent.needsConsent && !acceptRequiredSettings) || (consent.needsBackgroundConsent && !acceptBackground) || !acceptCoachSettings) return;
    const account = eligibility.account;
    if (!account?.current()) return;
    setJoining(true);
    try {
      const requiredCoachSettings = eligibility?.suggestedCoachAutoAcceptance || {};
      const result = await (inviteKind === 'email' ? backendService.acceptDojoEmailInvite(token, {
        ...joinConsentOptions(eligibility, acceptRequiredSettings, acceptBackground),
        acceptCoachAutoAcceptance: acceptCoachSettings,
        coachAutoAcceptanceChanges: requiredCoachSettings
      }) : backendService.joinDojoViaLink(token, {
        ...joinConsentOptions(eligibility, acceptRequiredSettings, acceptBackground),
        acceptCoachAutoAcceptance: acceptCoachSettings,
        coachAutoAcceptanceChanges: requiredCoachSettings
      }));
      if (!account.current()) return;
      setState(previous => ({
        ...previous,
        status: 'joined',
        currentDojo: { _id: result.dojo_id, name: result.dojo_name }
      }));
    } catch (error) {
      if (!account.current()) return;
      if (error.response?.status === 409 || /requirements or settings changed/i.test(error.response?.data?.error || '')) {
        try {
          const info = inviteKind === 'email' ? await backendService.getDojoEmailInviteInfo(token) : await backendService.getDojoJoinLinkInfo(token);
          const latest = await backendService.checkDojoEligibility(info.dojo._id);
          account.assert();
          setEligibility({ ...latest, account, requirements: latest.requirements || info.dojo.requirements, requirementsFingerprint: latest.requirementsFingerprint || info.dojo.requirementsFingerprint, requiredSettingsDelta: latest.requiredSettingsDelta || info.requiredSettingsDelta });
          setAcceptRequiredSettings(false); setAcceptBackground(false);
          setAcceptCoachSettings(false);
          setState(previous => ({ ...previous, status: 'ready', info, error: error.response?.data?.error || 'Dojo requirements changed. Review and consent again.' }));
        } catch (refreshError) {
          setState(previous => ({ ...previous, status: 'error', error: refreshError.response?.data?.error || 'Could not refresh dojo requirements.' }));
        }
        return;
      }
      setState(previous => ({
        ...previous,
        status: 'error',
        error: error.response?.data?.error || 'We could not join this dojo. Please try again.'
      }));
    } finally {
      if (account.current()) setJoining(false);
    }
  };

  if (!isSignedIn) {
    return <div className="dojo-invite-page"><h1>Dojo invitation</h1><p>Sign in to view and accept this dojo invitation.</p></div>;
  }
  if (!userSynced || state.status === 'loading') {
    return <div className="dojo-invite-page"><h1>Dojo invitation</h1><p>Loading dojo invitation…</p></div>;
  }
  if (state.status === 'error') {
    return <div className="dojo-invite-page"><h1>Dojo invitation</h1><p className="error">{state.error}</p></div>;
  }

  const dojo = state.info?.dojo;
  const current = state.currentDojo;
  const alreadyHere = current && dojo && String(current._id) === String(dojo._id);
  const inAnother = current && dojo && !alreadyHere;

  return (
    <div className="dojo-invite-page">
      <div className="dojo-invite-card">
        <div className="dojo-invite-emoji">{dojo?.emoji || '🥋'}</div>
        <h1>{state.status === 'joined' ? 'You joined the dojo' : 'Dojo invitation'}</h1>
        <h2>{dojo?.name || 'This dojo'}</h2>
        {state.status === 'joined' ? (
          <p>You are now a member of {dojo.name}.</p>
        ) : alreadyHere ? (
          <p>You are already a member of <strong>{dojo.name}</strong>. No action is needed.</p>
        ) : inAnother ? (
          <p>You are currently a member of <strong>{current.name}</strong>. Leave that dojo before accepting this invitation.</p>
        ) : (
          <>
            <p>You have been invited to join {dojo?.name}.</p>
            <DojoCoachMembershipNotice />
            {state.error && <p className="error" role="alert">{state.error}</p>}
            <DojoJoinSettingsConsent eligibility={eligibility} accepted={acceptRequiredSettings} onAccept={setAcceptRequiredSettings} backgroundAccepted={acceptBackground} onAcceptBackground={setAcceptBackground} />
            {eligibility?.requirements?.location_capture_enabled && <p role="alert">This dojo requires fresh device GPS when you finish each activity. If location permission is denied, the activity stays pending; gym tracking and saved gyms do not replace GPS.</p>}


            {eligibility?.suggestedCoachAutoAcceptance && Object.keys(eligibility.suggestedCoachAutoAcceptance).length > 0 && (
              <label className="dojo-invite-acceptance">
                <input type="checkbox" checked={acceptCoachSettings} onChange={(event) => setAcceptCoachSettings(event.target.checked)} />
                <span>I accept enabling the required coach settings before joining.</span>
              </label>
            )}

            {eligibility && joinSettingChanges(eligibility).blocking && (
              <p className="error">You do not currently meet this dojo&apos;s membership requirements.</p>
            )}
            <button
              type="button"
              onClick={join}
              disabled={joining || !eligibility || joinSettingChanges(eligibility).blocking || (joinSettingChanges(eligibility).needsConsent && !acceptRequiredSettings) || (joinSettingChanges(eligibility).needsBackgroundConsent && !acceptBackground) || !acceptCoachSettings}
            >
              {joining ? 'Joining…' : 'Join dojo'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default DojoInvitePage;
